/**
 * @module services/calendar-grid
 * @description Geometría de la grilla horaria de `CalendarView.vue` (vistas
 *              Día y Semana), extraída del componente para poder testearla
 *              sin montar el SFC (vue-jest no compila su template).
 */

/** @description Hora de inicio de la grilla */
export const GRID_START_HOUR = 7;
/** @description Hora de fin de la grilla */
export const GRID_END_HOUR = 21;
/** @description Pixeles por cada bloque de 30 minutos */
export const PX_PER_30MIN = 48;

/**
 * @description Hora (exclusiva) en que termina un slot dentro de su día:
 *              redondea hacia arriba (un slot que termina 21:14:59.999 ocupa
 *              la hora 21 → 22) y tope 24 si cruza la medianoche.
 * @param {Date} start
 * @param {Date} end
 * @returns {number}
 */
function slotEndHour(start, end) {
  if (end.toDateString() !== start.toDateString()) return 24;
  const partial =
    end.getMinutes() || end.getSeconds() || end.getMilliseconds() ? 1 : 0;
  return end.getHours() + partial;
}

/**
 * @description Rango horario que dibuja la grilla: 07 a 21 por default,
 *              extendido para cubrir cualquier slot cargado fuera de ese
 *              rango. UC-REG-269: con el rango fijo, un sobreturno antes de
 *              las 07:00 o desde las 21:00 se creaba pero no se veía. Sin
 *              slots fuera de 07–21 la grilla queda igual que siempre.
 * @param {Array<{startsAt: string|Date, endsAt?: string|Date}>} slots
 * @returns {{startHour: number, endHour: number}}
 */
export function getGridRange(slots = []) {
  let startHour = GRID_START_HOUR;
  let endHour = GRID_END_HOUR;
  slots.forEach((slot) => {
    if (!slot || !slot.startsAt) return;
    const start = new Date(slot.startsAt);
    if (isNaN(start)) return;
    const end = new Date(slot.endsAt || slot.startsAt);
    startHour = Math.min(startHour, start.getHours());
    if (!isNaN(end)) endHour = Math.max(endHour, slotEndHour(start, end));
  });
  return { startHour, endHour };
}

/**
 * @description Horas (etiquetas) de la grilla, de startHour a endHour - 1.
 * @param {{startHour: number, endHour: number}} range
 * @returns {Array<{hour: number, label: string}>}
 */
export function buildGridHours({ startHour, endHour }) {
  const hours = [];
  for (let h = startHour; h < endHour; h++) {
    hours.push({ hour: h, label: `${String(h).padStart(2, "0")}:00` });
  }
  return hours;
}

/**
 * @description Altura total de la grilla en px.
 * @param {{startHour: number, endHour: number}} range
 * @returns {number}
 */
export function gridHeightPx({ startHour, endHour }) {
  return (endHour - startHour) * PX_PER_30MIN * 2;
}

/**
 * @description Posición top (px) de un slot dentro de la grilla.
 * @param {{startsAt: string|Date}} slot
 * @param {number} startHour - Primera hora de la grilla
 * @returns {number}
 */
export function slotTopPx(slot, startHour) {
  const d = new Date(slot.startsAt);
  const totalMinutes = (d.getHours() - startHour) * 60 + d.getMinutes();
  return (totalMinutes / 30) * PX_PER_30MIN;
}

/**
 * @description Slot que el calendario agrega cuando el modal crea un
 *              sobreturno (el slot no existía en la grilla). UC-REG-271: el
 *              modal emite el slot ad-hoc original (startsAt/endsAt null), así
 *              que el horario sale de la cita que devolvió el API; sin esto el
 *              sobreturno no se veía hasta recargar.
 * @param {Object} slot - Slot emitido por el modal
 * @param {Object} appointment - Cita creada (respuesta del API)
 * @returns {Object}
 */
export function slotFromCreatedAppointment(slot, appointment) {
  return {
    ...slot,
    startsAt: appointment.startsAt || slot.startsAt,
    endsAt: appointment.endsAt || slot.endsAt,
    _id: (appointment.slot && appointment.slot._id) || appointment._id,
    available: false,
    appointment,
    isInstant: true,
  };
}
